import axios from "axios";
import DeviceDetector from "device-detector-js";
import QRCode from "qrcode";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { CONTEXT_TOKEN_PARAM, MOBILE_BREAKPOINT } from "../../../globalConstants";
import { NearestLocation, ReturnStatusComposite } from "../index";
import { formatDropoffAddress, formatLocationHours } from "../../DropoffMethods/helpers";
import { getDropoffLocationDisplayName } from "../../DropoffMethods/getDropoffLocationDisplayName";
import { clearLoadingIndicator, goToPage, setToken, showLoadingIndicator } from "../../../redux/app/actions";
import { reset } from "../../../redux/customer/actions";
import { DropoffMethodIDs } from "../../../redux/customer/types";
import { AnalyticCategories, ReturnStartedAndReturnStatusPageActions } from "../../../types/Analytics";
import { DataCyStrings } from "../../../types/DataCyStrings";
import { LabelType } from "../../../types/LabelType";
import { ReturnStatus } from "../../../types/ReturnStatus";
import { getLocationsContextTokenFromURL, handleEnterKeyPressOnClickHandlers } from "../../../utility";
import ga from "../../../utility/GAEmitter";
import getTranslator from "../../../utility/getTranslator";
import useSelector from "../../../utility/useTypedSelector";
import { useViewport } from "../../../utility/useViewport";
import { isAnonymizedValue } from "../../../utils/pii";

import PrimaryButton from "../../../components/Button/PrimaryButton";

import EmailModal from "../../../components/EmailModal";
import ExpressCodeApplePassButton from "../../../components/ExpressCodeApplePassButton";
import ExpressCodeGooglePassButton from "../../../components/ExpressCodeGooglePassButton";
import { defaultLoadingSymbol } from "../../../components/LoadingIndicator";
import { SVG } from "../../../components/svg";

import {
  $AnnotatedQrCodeText,
  $Body,
  $ButtonContents,
  $ConfirmationDetails,
  $ConfirmationDetailsHeader,
  $ConfirmationDetailsLink,
  $DisclaimerLocationMilesThreshold,
  $HappyReturnsBanner,
  $HappyReturnsLogo,
  $Header,
  $NearestLocationDetails,
  $NearestLocationDistance,
  $NearestLocationDivider,
  $NearestLocationName,
  $NearestLocationPromotion,
  $NearestLocationRow,
  $NearestLocationStatus,
  $NearestLocationStatusRow,
  $NearestLocationToggle,
  $NearestLocationTopRow,
  $NearestLocationsList,
  $NearestLocationsPanel,
  $NearestLocationsPanelHeader,
  $NearestLocationsSeeAllButton,
  $NextStepsCard,
  $NoLabelQrCodeContainer,
  $PrimaryDetails,
  $PrimaryDetailsLeftColumn,
  $PrimaryDetailsSingleColumn,
  $QRCard,
  $QRCardText,
  $QRCode,
} from "./styles";
import { $ScreenReaderOnlyComponent } from "../../ScreenReaderOnlyComponent/styles";
import { maskIfHashed } from "../../../utils/pii";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const Buffer = require("buffer/").Buffer;

// For explicit layout desktop-mobile layout differences, use: `useViewport()` and `MOBILE_BREAKPOINT`. See
// `PrivacyFooter` component for usage example.

const getDate = (timestamp?: string): Date | null => {
  if (!timestamp) {
    return null;
  }
  return new Date(timestamp);
};

const isAppleDevice = (): boolean => {
  const deviceDetector = new DeviceDetector();
  const device = deviceDetector.parse(navigator.userAgent);
  return device.device?.brand === "Apple";
};

const isAndroidDevice = (): boolean => {
  const deviceDetector = new DeviceDetector();
  const device = deviceDetector.parse(navigator.userAgent);
  return device.os?.name === "Android";
};

const isRetailerStoreApplePassRetailer = (retailerID: string | undefined): boolean => {
  if (retailerID === undefined) {
    return false;
  }
  return retailerID === "happy-returns-flow";
}

const Header = ({
  deliveryDate,
  departureDate,
  mostRecentReceivedAtDate,
  dropoffMethodID,
  expiryDate,
  returnlessRefund,
  returnStatus,
}: ReturnStatusComposite) => {
  const { t } = getTranslator("NextStepsCard")();
  const deliveryDateObject = getDate(deliveryDate);
  const departureDateObject = getDate(departureDate);
  const expiryDateObject = getDate(expiryDate);
  const mostRecentReceivedAtDateObject = getDate(mostRecentReceivedAtDate);

  if (returnlessRefund) {
    return <$Header data-cy={DataCyStrings.nextStepsHeaderReturnless}>{t("headerReturnless")}</$Header>;
  }
  if (returnStatus === ReturnStatus.expired && expiryDateObject) {
    return (
      <$Header data-cy={DataCyStrings.nextStepsHeaderExpired}>
        {t("headerExpired", {
          month: t(`month.${expiryDateObject.getMonth()}`),
          date: expiryDateObject.getDate(),
          year: expiryDateObject.getFullYear(),
        })}
      </$Header>
    );
  }
  if (deliveryDateObject) {
    return (
      <$Header data-cy={DataCyStrings.nextStepsHeaderDelivered}>
        {t("headerDelivered", {
          month: t(`month.${deliveryDateObject.getMonth()}`),
          date: deliveryDateObject.getDate(),
          year: deliveryDateObject.getFullYear(),
        })}
      </$Header>
    );
  }
  if (departureDateObject) {
    return (
      <$Header data-cy={DataCyStrings.nextStepsHeaderDeparted}>
        {t("headerDeparted", {
          month: t(`month.${departureDateObject.getMonth()}`),
          date: departureDateObject.getDate(),
          year: departureDateObject.getFullYear(),
        })}
      </$Header>
    );
  }

  if (dropoffMethodID === DropoffMethodIDs.returnBar && returnStatus === ReturnStatus.droppedOff && mostRecentReceivedAtDateObject) {
    return (
      <$Header data-cy={DataCyStrings.nextStepsHeaderDroppedOff}>
        {t("headerDroppedOff", {
          month: t(`month.${mostRecentReceivedAtDateObject.getMonth()}`),
          date: mostRecentReceivedAtDateObject.getDate(),
          year: mostRecentReceivedAtDateObject.getFullYear(),
        })}
      </$Header>
    );
  }


  if ((returnStatus === ReturnStatus.started || returnStatus === ReturnStatus.droppedOff) && expiryDateObject) {
    let key = "headerStartedReturnBar";
    let dataCy = DataCyStrings.nextStepsHeaderStartedReturnBar;
    if (dropoffMethodID === DropoffMethodIDs.retailerStore) {
      key = "headerStartedInStore";
      dataCy = DataCyStrings.nextStepsHeaderStartedInStore;
    } else if (dropoffMethodID === DropoffMethodIDs.mail || dropoffMethodID === DropoffMethodIDs.mailShopperProvided) {
      key = "headerStartedMail";
      dataCy = DataCyStrings.nextStepsHeaderStartedMail;
    } else if (dropoffMethodID === DropoffMethodIDs.mailNoBoxNoLabel) {
      key = "headerStartedMailNoBoxNoLabel";
      dataCy = DataCyStrings.nextStepsHeaderStartedMailNoBoxNoLabel;
    } else if (dropoffMethodID === DropoffMethodIDs.mailNoLabel) {
      key = "headerStartedMailNoLabel"
      dataCy = DataCyStrings.nextStepsHeaderStartedMailNoLabel
    }

    return (
      <$Header data-cy={dataCy}>
        <$ScreenReaderOnlyComponent as="h2">{t("nextSteps")}</$ScreenReaderOnlyComponent>
        {t(key, {
          month: t(`month.${expiryDateObject.getMonth()}`),
          date: expiryDateObject.getDate(),
          year: expiryDateObject.getFullYear(),
        })}
      </$Header>
    );
  }

  return <></>;
};

const Body = ({
  deliveryDate,
  departureDate,
  mostRecentReceivedAtDate,
  dropoffMethodID,
  retailerName,
  returnlessRefund,
  returnStatus,
  selectedLabelType,
}: ReturnStatusComposite) => {
  const { t } = getTranslator("NextStepsCard")();
  const deliveryDateObject = getDate(deliveryDate);
  const departureDateObject = getDate(departureDate);

  if (returnlessRefund) {
    return <$Body data-cy={DataCyStrings.nextStepsBodyReturnless}>{t("bodyReturnless")}</$Body>;
  }
  if (returnStatus === ReturnStatus.expired) {
    return <$Body data-cy={DataCyStrings.nextStepsBodyExpired}>{t("bodyExpired")}</$Body>;
  }
  if (deliveryDateObject) {
    return (
      <$Body data-cy={DataCyStrings.nextStepsBodyDelivered}>{t("bodyReturnIsBeingProcessed", { retailerName })}</$Body>
    );
  }
  if (departureDateObject) {
    return <$Body data-cy={DataCyStrings.nextStepsBodyDeparted}>{t("bodyReturnWillBeProcessed")}</$Body>;
  }

  if (dropoffMethodID === DropoffMethodIDs.returnBar && returnStatus === ReturnStatus.droppedOff && mostRecentReceivedAtDate) {
    return <$Body data-cy={DataCyStrings.nextStepsBodyDeparted}>{t("bodyReturnBarDroppedOff")}</$Body>;
  }

  if (returnStatus === ReturnStatus.started || returnStatus === ReturnStatus.droppedOff) {
    if (dropoffMethodID === DropoffMethodIDs.mail) {
      if (selectedLabelType === LabelType.printerlessLabel) {
        // show EU variant
        return <$Body data-cy={DataCyStrings.nextStepsBodyStartedQRCode}>{t("bodyQRCode")}</$Body>;
      }
      return <$Body data-cy={DataCyStrings.nextStepsBodyStartedShippingLabel}>{t("bodyShippingLabel")}</$Body>;
    } else if (dropoffMethodID === DropoffMethodIDs.mailShopperProvided) {
      return <$Body data-cy={DataCyStrings.nextStepsBodyStartedPackingSlip}>{t("bodyPackingSlip")}</$Body>;
    } else if (dropoffMethodID === DropoffMethodIDs.mailNoBoxNoLabel || dropoffMethodID === DropoffMethodIDs.mailNoLabel) {
      return (
        <$Body data-cy={DataCyStrings.nextStepsBodyStartedMailNoLabel}>
          {t("bodyMailNoLabel")}
        </$Body>
      );
    }
    
    // default to return bar / in-store copy
    return (
      <$Body>
        <ul>
          <li data-cy={DataCyStrings.nextStepsBodyStartedShippingMaterials}>{t("bodyShippingMaterials")}</li>
          <li data-cy={DataCyStrings.nextStepsBodyStartedShoes}>{t("bodyShoes")}</li>
          <li data-cy={DataCyStrings.nextStepsBodyStartedItemBarcodes}>{t("bodyItemBarcodes")}</li>
        </ul>
      </$Body>
    );
  }
  return <></>;
};

// TODO refactor, reduce code duplication
// TODO CF-994 button color state based on open draft order
// TODO CF-994 icon
const Button = ({
  deliveryDate,
  departureDate,
  dropoffMethodID,
  retailerID,
  returnID,
  returnlessRefund,
  returnStatus,
  selectedLabelType,
  shipmentTrackingURL,
  storeLocatorURL,
  mostRecentReceivedAtDate,
  zipCode,
  latitude,
  longitude,
  nearestLocations,
  returnBarLocationsABGroup,
}: ReturnStatusComposite) => {
  const dispatch = useDispatch();
  const { t } = getTranslator("NextStepsCard")();
  const deliveryDateObject = getDate(deliveryDate);
  const departureDateObject = getDate(departureDate);

  if (returnlessRefund) {
    return <></>;
  }

  if (dropoffMethodID === DropoffMethodIDs.returnBar && returnStatus === ReturnStatus.droppedOff && mostRecentReceivedAtDate) {
    return <></>;
  }

  if (returnStatus === ReturnStatus.expired) {
    return (
      <PrimaryButton
        dataCyString={DataCyStrings.nextStepsButtonStartNewReturn}
        onButtonClick={() => {
          ga.event({
            category: AnalyticCategories.ReturnStatusPage,
            action: ReturnStartedAndReturnStatusPageActions.StartANewReturn,
          });
          window.history.replaceState(null, document.title, window.location.pathname);
          dispatch(reset());
          dispatch(setToken(""));
          dispatch(goToPage("loginReturnPortal"));
        }}
        width={"auto"}
      >
        <$ButtonContents>
          <div>{t("callToActionStartNewReturn")}</div>
        </$ButtonContents>
      </PrimaryButton>
    );
  }
  if (departureDateObject || deliveryDateObject) {
    if (shipmentTrackingURL) {
      // URLs must begin with https://, https://, or omit a leading www.
      try {
        const validatedShipmentTrackingURL = new URL(shipmentTrackingURL).href;
        return (
          <PrimaryButton
            dataCyString={DataCyStrings.nextStepsButtonTrackReturn}
            onButtonClick={() => {
              ga.event({
                category: AnalyticCategories.ReturnStatusPage,
                action: ReturnStartedAndReturnStatusPageActions.TrackReturn,
              });
              window.open(validatedShipmentTrackingURL, "_blank");
            }}
            width={"auto"}
          >
            <$ButtonContents>
              <div>{t("callToActionTrackReturn")}</div>
              <SVG name="external-link" />
            </$ButtonContents>
          </PrimaryButton>
        );
      } catch (error) {
        console.error(error); // TODO CF-994 Datadog logger
      }
    }
    return <></>;
  }
  if (returnStatus === ReturnStatus.started || returnStatus === ReturnStatus.droppedOff) {
    if (dropoffMethodID === DropoffMethodIDs.mail) {
      if (selectedLabelType === LabelType.printerlessLabel) {
        return <></>;
      }
      return (
        <PrimaryButton
          dataCyString={DataCyStrings.nextStepsButtonShippingLabel}
          onButtonClick={() => {
            ga.event({
              category: AnalyticCategories.ReturnStatusPage,
              action: ReturnStartedAndReturnStatusPageActions.DownloadShippingLabel,
            });
            window.open(`/shippinglabel/${returnID}`, "_blank");
          }}
          width={"auto"}
        >
          <$ButtonContents>
            <SVG name="printer" />
            <div>{t("callToActionShippingLabel")}</div>
          </$ButtonContents>
        </PrimaryButton>
      );
    }
    if (dropoffMethodID === DropoffMethodIDs.mailShopperProvided) {
      return (
        <PrimaryButton
          dataCyString={DataCyStrings.nextStepsButtonPackingSlip}
          onButtonClick={() => {
            ga.event({
              category: AnalyticCategories.ReturnStatusPage,
              action: ReturnStartedAndReturnStatusPageActions.DownloadPackingSlip,
            });
            window.open(`/packingslip/${returnID}`, "_blank");
          }}
          width={"auto"}
        >
          <$ButtonContents>
            <SVG name="printer" />
            <div>{t("callToActionPackingSlip")}</div>
          </$ButtonContents>
        </PrimaryButton>
      );
    }
    if (dropoffMethodID === DropoffMethodIDs.retailerStore && storeLocatorURL) {
      try {
        // URLs must begin with https://, https://, or omit a leading www.
        const validatedStoreLocatorURL = new URL(storeLocatorURL).href;
        return (
          <PrimaryButton
            dataCyString={DataCyStrings.nextStepsButtonFindLocation}
            onButtonClick={() => {
              ga.event({
                category: AnalyticCategories.ReturnStatusPage,
                action: ReturnStartedAndReturnStatusPageActions.SeeAllLocations,
              });
              window.open(validatedStoreLocatorURL, "_blank");
            }}
            width={"auto"}
          >
            <$ButtonContents>
              <div>{t("callToActionLocation")}</div>
              <SVG name="external-link" />
            </$ButtonContents>
          </PrimaryButton>
        );
      } catch (err) {
        console.error(err); // TODO CF-994 Datadog logger
      }
      return <></>;
    }

    if (dropoffMethodID === DropoffMethodIDs.returnBar) {
      // NTK-1930 treatment: the panel above owns the "See all" button, so suppress this duplicate.
      if (returnBarLocationsABGroup === "test" && nearestLocations && nearestLocations.length > 0) {
        return <></>;
      }
      return (
        <PrimaryButton
          dataCyString={DataCyStrings.nextStepsButtonFindReturnBar}
          onButtonClick={() => {
            ga.event({
              category: AnalyticCategories.ReturnStatusPage,
              action: ReturnStartedAndReturnStatusPageActions.SeeAllLocations,
            });
            if (returnBarLocationsABGroup) {
              reportSeeAllClickBeacon(returnID);
            }
            let locationsURL = `https://locations.happyreturns.com?has_qr_code=true&retailer=${retailerID}`;
            // Only include address if zipCode is not anonymized/redacted
            if (zipCode && !isAnonymizedValue(zipCode)) {
              locationsURL += `&address=${zipCode}`;
            }
            if (latitude && longitude) {
              locationsURL += `&latitude=${latitude}&longitude=${longitude}`;
            }
            locationsURL += `&source=return_status`;
            const locationsContextToken = getLocationsContextTokenFromURL();
            if (locationsContextToken) {
              locationsURL += `&${CONTEXT_TOKEN_PARAM}=${encodeURIComponent(locationsContextToken)}`;
            }
            window.open(locationsURL, "_blank");
          }}
          width={"auto"}
        >
          <$ButtonContents>
            <div>{t("callToActionReturnBar")}</div>
            <SVG name="external-link" />
          </$ButtonContents>
        </PrimaryButton>
      );
    }

    if (dropoffMethodID === DropoffMethodIDs.mailNoBoxNoLabel) {
      if (nearestLocations && nearestLocations.length > 0) {
        return <></>;
      }
      return (
        <PrimaryButton
          dataCyString={DataCyStrings.nextStepsButtonFindUPSStores}
          onButtonClick={() => {
            ga.event({
              category: AnalyticCategories.ReturnStatusPage,
              action: ReturnStartedAndReturnStatusPageActions.SeeAllMailNoBoxNoLabelLocations,
            });

            const locationsURL = "https://locations.happyreturns.com/no-print"
            window.open(
              locationsURL,
              "_blank"
            );
          }}
          width={"auto"}
        >
          <$ButtonContents>
            <div>{t("callToActionNonConsolidatedLocation")}</div>
            <SVG name="external-link" />
          </$ButtonContents>
        </PrimaryButton>
      );
    }

    if (dropoffMethodID === DropoffMethodIDs.mailNoLabel) {
      if (nearestLocations && nearestLocations.length > 0) {
        return <></>;
      }
      return (
        <PrimaryButton
          dataCyString={DataCyStrings.nextStepsButtonFindUPSStores}
          onButtonClick={() => {
            ga.event({
              category: AnalyticCategories.ReturnStatusPage,
              action: ReturnStartedAndReturnStatusPageActions.SeeAllMailNoLabelLocations,
            });

            const locationsURL = "https://locations.happyreturns.com/no-print"
            window.open(
              locationsURL,
              "_blank"
            );
          }}
          width={"auto"}
        >
          <$ButtonContents>
            <div>{t("callToActionNonConsolidatedLocation")}</div>
            <SVG name="external-link" />
          </$ButtonContents>
        </PrimaryButton>
      );
    }
  }
  return <></>;
};

// NTK-1930: fire-and-forget server-side beacon for the "See all" click (CTR numerator).
const reportSeeAllClickBeacon = (returnID?: string): void => {
  if (!returnID) {
    return;
  }
  const returnStatusToken = new URLSearchParams(window.location.search).get("authorization");
  // The endpoint requires auth; skip the beacon rather than firing a request we already know will 401.
  if (!returnStatusToken) {
    return;
  }
  axios
    .post(`returnstatus/${returnID}/see-all-click`, null, {
      headers: { Authorization: `Bearer ${returnStatusToken}` },
    })
    .catch(() => {
      /* non-critical analytics beacon */
    });
};

const buildNoPrintLocationsURL = ({
  zipCode,
  latitude,
  longitude,
}: {
  zipCode?: string;
  latitude?: number;
  longitude?: number;
}): string => {
  const base = "https://locations.happyreturns.com/no-print";
  const params: string[] = [];
  if (zipCode && !isAnonymizedValue(zipCode)) {
    params.push(`address=${zipCode}`);
  }
  if (latitude != null && longitude != null) {
    params.push(`latitude=${latitude}&longitude=${longitude}`);
  }
  return params.length ? `${base}?${params.join("&")}` : base;
};

const renderLocationMapsURL = (location: NearestLocation): string => {
  return (
    "https://www.google.com/maps/search/?api=1&query=" +
    encodeURIComponent(formatDropoffAddress(location.address))
  );
};

const findPromotionDisplayElement = (location: NearestLocation): string | undefined => {
  if (!location.display) {
    return undefined;
  }
  const promo = location.display.find((row) => row.label === "Promotions");
  return promo?.value;
};

const NearestLocationRow = ({
  location,
  index,
  expanded,
  onToggle,
}: {
  location: NearestLocation;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}) => {
  // Reuse the already-translated DropoffMethods strings for these common words rather than adding
  // (untranslated) NextStepsCard copies. NextStepsCard.{showMore,showLess,hoursMayVaryDuring} have no
  // translations, so they'd fall back to English; DropoffMethods has them translated in every locale.
  const { t: tDropoffMethods } = getTranslator("DropoffMethods")();
  const parsedHours = formatLocationHours(location.openingHours || [], location.timeZone || "");
  const promotion = findPromotionDisplayElement(location);
  // useDisplayNameOverrideForLocationName is not currently surfaced via the
  // Return Status response; default to false (use canonical name). If it
  // becomes part of the contract later, plumb it through ReturnStatusComposite.
  const displayName = getDropoffLocationDisplayName(false, {
    name: location.name,
    displayNameOverride: location.displayNameOverride,
  });
  const mapsHref = renderLocationMapsURL(location);

  return (
    <$NearestLocationRow
      data-cy={`${DataCyStrings.nextStepsNearestLocationRow}-${index}`}
      data-index={index}
    >
      <$NearestLocationTopRow>
        <$NearestLocationName data-cy={`${DataCyStrings.nextStepsNearestLocationName}-${index}`}>
          {displayName}
        </$NearestLocationName>
        <$NearestLocationDistance
          data-cy={`${DataCyStrings.nextStepsNearestLocationDistance}-${index}`}
          href={mapsHref}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => {
            ga.event({
              category: AnalyticCategories.ReturnStatusPage,
              action: ReturnStartedAndReturnStatusPageActions.StoreMileage,
            });
            e.stopPropagation();
          }}
        >
          {location.distance?.toFixed(1)} mi
        </$NearestLocationDistance>
      </$NearestLocationTopRow>

      <$NearestLocationStatusRow>
        {parsedHours.status && (
          <$NearestLocationStatus
            data-cy={`${DataCyStrings.nextStepsNearestLocationStatus}-${index}`}
          >
            {parsedHours.status}
          </$NearestLocationStatus>
        )}
        <$NearestLocationToggle
          type="button"
          data-cy={`${DataCyStrings.nextStepsNearestLocationToggle}-${index}`}
          onClick={(e) => {
            ga.event({
              category: AnalyticCategories.ReturnStatusPage,
              action: expanded
                ? ReturnStartedAndReturnStatusPageActions.ShowLess
                : ReturnStartedAndReturnStatusPageActions.ShowMore,
              label: `${index}`,
            });
            onToggle();
            e.stopPropagation();
          }}
        >
          {expanded ? tDropoffMethods("showLess") : tDropoffMethods("showMore")}
          <SVG name={expanded ? "up" : "down"} />
        </$NearestLocationToggle>
      </$NearestLocationStatusRow>

      {expanded && parsedHours.schedule && parsedHours.schedule.length > 0 && (
        <$NearestLocationDetails>
          <SVG name="clock" />
          <div>
            {parsedHours.schedule.map((row, i) => (
              <div key={i}>{row}</div>
            ))}
            {location.hoursMayVary && <div>{tDropoffMethods("hoursMayVaryDuring")}</div>}
          </div>
        </$NearestLocationDetails>
      )}
      {expanded && location.address && (
        <$NearestLocationDetails>
          <SVG name="location" />
          <div>
            <a target="_blank" href={mapsHref} rel="noreferrer">
              {formatDropoffAddress(location.address)}
            </a>
          </div>
        </$NearestLocationDetails>
      )}

      {promotion && (
        <$NearestLocationPromotion
          data-cy={`${DataCyStrings.nextStepsNearestLocationPromotion}-${index}`}
        >
          <SVG name="tag" />
          <div>{promotion}</div>
        </$NearestLocationPromotion>
      )}
    </$NearestLocationRow>
  );
};

const NearestLocationsPanel = ({
  nearestLocations,
  dropoffMethodID,
  zipCode,
  latitude,
  longitude,
  retailerID,
  returnID,
  returnBarLocationsABGroup,
}: ReturnStatusComposite) => {
  const { t } = getTranslator("NextStepsCard")();
  const [expandedIndex, setExpandedIndex] = useState<Record<number, boolean>>({});

  const isNoLabelMethod =
    dropoffMethodID === DropoffMethodIDs.mailNoLabel ||
    dropoffMethodID === DropoffMethodIDs.mailNoBoxNoLabel;
  // NTK-1930: also show the panel for Return Bar treatment (backend only sends nearestLocations then).
  const isReturnBarTreatment =
    dropoffMethodID === DropoffMethodIDs.returnBar &&
    returnBarLocationsABGroup === "test";
  if ((!isNoLabelMethod && !isReturnBarTreatment) || !nearestLocations || nearestLocations.length === 0) {
    return <></>;
  }

  let seeAllAction: ReturnStartedAndReturnStatusPageActions;
  if (isReturnBarTreatment) {
    seeAllAction = ReturnStartedAndReturnStatusPageActions.SeeAllReturnBarLocations;
  } else if (dropoffMethodID === DropoffMethodIDs.mailNoLabel) {
    seeAllAction = ReturnStartedAndReturnStatusPageActions.SeeAllMailNoLabelLocations;
  } else {
    seeAllAction = ReturnStartedAndReturnStatusPageActions.SeeAllMailNoBoxNoLabelLocations;
  }

  let seeAllURL: string;
  if (isReturnBarTreatment) {
    seeAllURL = `https://locations.happyreturns.com?has_qr_code=true&retailer=${retailerID}`;
    if (zipCode && !isAnonymizedValue(zipCode)) {
      seeAllURL += `&address=${zipCode}`;
    }
    if (latitude && longitude) {
      seeAllURL += `&latitude=${latitude}&longitude=${longitude}`;
    }
    seeAllURL += `&source=return_status`;
    const locationsContextToken = getLocationsContextTokenFromURL();
    if (locationsContextToken) {
      seeAllURL += `&${CONTEXT_TOKEN_PARAM}=${encodeURIComponent(locationsContextToken)}`;
    }
  } else {
    seeAllURL = buildNoPrintLocationsURL({ zipCode, latitude, longitude });
  }

  return (
    <$NearestLocationsPanel data-cy={DataCyStrings.nextStepsNearestLocationsPanel}>
      <$NearestLocationsPanelHeader>
        {t("nearbyShippingLocations")}
      </$NearestLocationsPanelHeader>
      <$NearestLocationsList>
        {nearestLocations.map((location, idx) => (
          <React.Fragment key={`${location.name}-${idx}`}>
            {idx > 0 && <$NearestLocationDivider />}
            <NearestLocationRow
              location={location}
              index={idx}
              expanded={!!expandedIndex[idx]}
              onToggle={() =>
                setExpandedIndex((prev) => ({ ...prev, [idx]: !prev[idx] }))
              }
            />
          </React.Fragment>
        ))}
      </$NearestLocationsList>
      <$NearestLocationsSeeAllButton>
        <PrimaryButton
          dataCyString={DataCyStrings.nextStepsNearestLocationsSeeAll}
          onButtonClick={() => {
            ga.event({
              category: AnalyticCategories.ReturnStatusPage,
              action: seeAllAction,
            });
            if (isReturnBarTreatment) {
              reportSeeAllClickBeacon(returnID);
            }
            window.open(seeAllURL, "_blank");
          }}
          width={"auto"}
        >
          <$ButtonContents>
            <div>{isReturnBarTreatment ? t("callToActionReturnBar") : t("callToActionMailNoLabelLocations")}</div>
            <SVG name="external-link" />
          </$ButtonContents>
        </PrimaryButton>
      </$NearestLocationsSeeAllButton>
    </$NearestLocationsPanel>
  );
};

const Disclaimer = ({
  dropoffMethodID,
  locationBlockMilesThreshold,
  returnStatus
}: ReturnStatusComposite) => {
  const { t } = getTranslator("NextStepsCard")();

  if (returnStatus === ReturnStatus.started && dropoffMethodID === DropoffMethodIDs.returnBar) {
    return (
      <$DisclaimerLocationMilesThreshold
        data-cy={DataCyStrings.nextStepsBodyStartedLocationMilesThreshold}>
        {t("bodyLocationBlockMilesThreshold", {
          locationBlockMilesThreshold: locationBlockMilesThreshold,
        })}
      </$DisclaimerLocationMilesThreshold>
    );
  }

  return <></>;
};

const QRCard = ({
  departureDate,
  dropoffMethodID,
  eatn,
  expressCode,
  expiryDate,
  numItemsInReturn,
  retailerName,
  retailerID,
  returnID,
  returnlessRefund,
  returnStatus,
  selectedCarrierName,
  selectedLabelType,
}: ReturnStatusComposite) => {
  const dispatch = useDispatch();
  const expiryDateObject = getDate(expiryDate);

  const [QRCodeDataURL, setQRCodeDataURL] = useState("");
  const [showBleckmannQRCode, setShowBleckmannQRCode] = useState(false)
  const { t } = getTranslator("NextStepsCard")();

  useEffect(() => {
    if (
      dropoffMethodID !== DropoffMethodIDs.returnBar &&
      dropoffMethodID !== DropoffMethodIDs.retailerStore &&
      !expressCode
    ) {
      return;
    }

    // If there is carrier selection(bleckmann), don't show the default QR code
    if (!!selectedCarrierName) {
      return;
    }

    const generateQRCodeDataURL = async (expressCode) => {
      try {
        const URL = await QRCode.toDataURL(expressCode);
        setQRCodeDataURL(URL);
      } catch (err) {
        console.error(err); // TODO CF-994 Datadog logger
      }
    };
    generateQRCodeDataURL(expressCode);
  }, [expressCode]);

  // Printerless (QR code) mail labels differ from our typical QR codes.
  // Instead of generating them on-the-fly, we store URLs to these mail labels, like our traditional printer labels.
  // In a departure from the previous `ReturnStarted` page, instead of embedding the URL directly in an img element,
  // we explicitly query the relevant endpoint and convert the response such that we can ultimately reuse the same
  // `QRCodeDataURL` state variable used for Express Code QR codes.
  // This explicit querying allows us to display a loading indicator, to more gracefully handle errors, etc.
  useEffect(() => {
    const getPrinterlessQRCode = async () => {
      const response = await axios.get(`/qrcode/${returnID}`, { responseType: "arraybuffer" });
      const base64Response = Buffer.from(response.data, "binary").toString("base64");
      setQRCodeDataURL(`data:image;base64,${base64Response}`);
    }

    const getBleckmannQRCode = async () => {
      if (!returnID || !selectedCarrierName) {
        return;
      }
      if (dropoffMethodID !== DropoffMethodIDs.mail && selectedLabelType !== LabelType.printerlessLabel) {
        return;
      }
      // If the departure date exists, do not call the /qrcode api or show the bleckmann QR code
      if (departureDate) {
        return
      }
      // TODO CF-1202 re-implement retry behavior
      // TODO optimization, avoid re-fetch when switching between desktop and mobile layouts
      try {
        dispatch(showLoadingIndicator(defaultLoadingSymbol));
        await getPrinterlessQRCode();
        setShowBleckmannQRCode(true)
      } catch (err) {
        console.error(err); // TODO CF-994 Datadog logger
        setShowBleckmannQRCode(false)
      } finally {
        dispatch(clearLoadingIndicator());
      }
    }

    getBleckmannQRCode();
  }, [returnID]);

  if (returnlessRefund || returnStatus == ReturnStatus.expired) {
    return <></>;
  }
  if (dropoffMethodID === DropoffMethodIDs.returnBar && returnStatus !== ReturnStatus.droppedOff && QRCodeDataURL) {
    return (
      <$QRCard data-cy={DataCyStrings.nextStepsQRCard}>
        <$HappyReturnsBanner>
          <$HappyReturnsLogo>
            <SVG name={"hr-logo-happyreturns"} />
          </$HappyReturnsLogo>
        </$HappyReturnsBanner>
        <img src={QRCodeDataURL} alt={expressCode} data-cy={DataCyStrings.nextStepsQRCodeImageExpressCode} />
        <$QRCardText data-cy={DataCyStrings.nextStepsQRCodeText}>{expressCode}</$QRCardText>
        {isAppleDevice() && expiryDateObject && expressCode && numItemsInReturn && retailerName && returnID && (
          <ExpressCodeApplePassButton
            confirmationCode={expressCode}
            expirationDate={expiryDateObject}
            numItemsInReturn={numItemsInReturn}
            retailerName={retailerName}
            returnID={returnID}
            // We use changeDropoffMethodReturnID instead of returnID to use the unauthenticated endpoint
            changeDropoffMethodReturnID={returnID}
          />
        )}
        {isAndroidDevice() && returnID && (
          <ExpressCodeGooglePassButton
            returnID={returnID}
          />
        )}
      </$QRCard>
    );
  }
  if (dropoffMethodID === DropoffMethodIDs.retailerStore && QRCodeDataURL) {
    return (
      <$QRCard data-cy={DataCyStrings.nextStepsQRCard}>
        <img src={QRCodeDataURL} alt={expressCode} data-cy={DataCyStrings.nextStepsQRCodeImageExpressCode} />
        <$QRCardText data-cy={DataCyStrings.nextStepsQRCodeText}>{expressCode}</$QRCardText>
        {isAppleDevice() && expiryDateObject && expressCode && numItemsInReturn && retailerName && returnID && (
          <ExpressCodeApplePassButton
            confirmationCode={expressCode}
            expirationDate={expiryDateObject}
            numItemsInReturn={numItemsInReturn}
            retailerName={retailerName}
            returnID={returnID}
            // We use changeDropoffMethodReturnID instead of returnID to use the unauthenticated endpoint
            changeDropoffMethodReturnID={returnID}
          />
        )}
        {isAndroidDevice() && returnID && (
          <ExpressCodeGooglePassButton
            returnID={returnID}
          />
        )}
      </$QRCard>
    );
  }
  if (dropoffMethodID === DropoffMethodIDs.mail && selectedLabelType === LabelType.printerlessLabel && showBleckmannQRCode) {
    return (
      <$QRCard data-cy={DataCyStrings.nextStepsQRCard}>
        <$QRCode src={QRCodeDataURL} alt={""} data-cy={DataCyStrings.nextStepsQRCodeImageMailLabel} />
        <$QRCardText data-cy={DataCyStrings.nextStepsQRCarrierText}>{selectedCarrierName}</$QRCardText>
      </$QRCard>
    );
  }

  if (dropoffMethodID === DropoffMethodIDs.mailNoBoxNoLabel && QRCodeDataURL) {
    return (
      <$QRCard data-cy={DataCyStrings.nextStepsQRCard}>
        <$AnnotatedQrCodeText data-cy={DataCyStrings.nextStepsQRAnnotationCard}>No Pack Or Print</$AnnotatedQrCodeText>
        <img src={QRCodeDataURL} alt={expressCode} data-cy={DataCyStrings.nextStepsQRCodeImageExpressCode} />
        <$QRCardText data-cy={DataCyStrings.nextStepsQRCodeText}>{expressCode}</$QRCardText>
        {isAppleDevice() && expiryDateObject && expressCode && numItemsInReturn && retailerName && returnID && (
          <ExpressCodeApplePassButton
            confirmationCode={expressCode}
            expirationDate={expiryDateObject}
            numItemsInReturn={numItemsInReturn}
            retailerName={retailerName}
            returnID={returnID}
            // We use changeDropoffMethodReturnID instead of returnID to use the unauthenticated endpoint
            changeDropoffMethodReturnID={returnID}
          />
        )}
        {isAndroidDevice() && returnID && (
          <ExpressCodeGooglePassButton
            returnID={returnID}
          />
        )}
      </$QRCard>
    );
  }

  if (dropoffMethodID === DropoffMethodIDs.mailNoLabel && QRCodeDataURL) {
    return (
      <$QRCard data-cy={DataCyStrings.nextStepsQRCard}>
        <$AnnotatedQrCodeText data-cy={DataCyStrings.nextStepsQRAnnotationCard}>No Print</$AnnotatedQrCodeText>
        <$NoLabelQrCodeContainer>
          <img src={QRCodeDataURL} alt={expressCode} data-cy={DataCyStrings.nextStepsQRCodeImageExpressCode} />
        </$NoLabelQrCodeContainer>
        {expressCode && <$QRCardText data-cy={DataCyStrings.nextStepsQRCodeText}>{expressCode}</$QRCardText>}
        {isAppleDevice() && expiryDateObject && expressCode && numItemsInReturn && retailerName && returnID && eatn && (
          <ExpressCodeApplePassButton
            confirmationCode={expressCode}
            expirationDate={expiryDateObject}
            numItemsInReturn={numItemsInReturn}
            retailerName={retailerName}
            returnID={returnID}
            // We use changeDropoffMethodReturnID instead of returnID to use the unauthenticated endpoint
            changeDropoffMethodReturnID={returnID}
          />
        )}
        {isAndroidDevice() && returnID && (
          <ExpressCodeGooglePassButton
            returnID={returnID}
          />
        )}
      </$QRCard>
    );
  }

  return <></>;
};

const ConfirmationDetails = ({
  deliveryDate,
  departureDate,
  emailAddress,
  mostRecentReceivedAtDate,
  otherDropoffMethodsAvailable,
  returnlessRefund,
  dropoffMethodID,
  returnID,
  returnStatus,
}: ReturnStatusComposite) => {
  const { t } = getTranslator("NextStepsCard")();
  const { locale } = useSelector((store) => store.app);
  const deliveryDateObject = getDate(deliveryDate);
  const departureDateObject = getDate(departureDate);

  const [emailModalOpen, setEmailModalOpen] = useState(false);

  if (!emailAddress) {
    return <></>;
  }
  if (returnStatus === ReturnStatus.expired) {
    return <></>;
  }
  if (deliveryDateObject || departureDateObject) {
    return <></>;
  }

  if (dropoffMethodID === DropoffMethodIDs.returnBar && returnStatus === ReturnStatus.droppedOff && mostRecentReceivedAtDate) {
    return <></>;
  }

  // Mask hashed emails to show "(redacted)" instead of the hash
  const maskedEmailAddress = maskIfHashed(emailAddress);

  return (
    <$ConfirmationDetails>
      <$ScreenReaderOnlyComponent as="h2">{t("confirmationSent")}</$ScreenReaderOnlyComponent>
      <$ConfirmationDetailsHeader data-cy={DataCyStrings.nextStepsConfirmationDetailsHeader}>
        {t("confirmationDetailsHeader", { emailAddress: maskedEmailAddress })}
      </$ConfirmationDetailsHeader>
      <$ConfirmationDetailsLink>
        <SVG name={"envelope-check"} />
        <a
          {...handleEnterKeyPressOnClickHandlers(() => {
            ga.event({
              category: AnalyticCategories.ReturnStatusPage,
              action: ReturnStartedAndReturnStatusPageActions.SendToAnotherEmail,
            });
            setEmailModalOpen(true);
          })}
          data-cy={DataCyStrings.nextStepsConfirmationDetailsResendEmailLink}
        >
          {t("confirmationDetailsSendToAnotherEmail")}
        </a>
      </$ConfirmationDetailsLink>
      <ResendEmailModal isOpen={emailModalOpen} onClose={() => setEmailModalOpen(false)} returnID={returnID} />
    </$ConfirmationDetails>
  );
};

const ResendEmailModal = ({
  isOpen,
  onClose,
  returnID,
}: {
  isOpen: boolean;
  onClose: () => void;
  returnID: string | undefined;
}) => {
  const { t } = getTranslator("NextStepsCard")();
  const dispatch = useDispatch();

  if (!returnID) {
    return <></>;
  }

  return (
    <EmailModal
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={() => { }} // TODO Fix warning
      onSubmit={async ({ email, setError, onSuccess }) => {
        dispatch(showLoadingIndicator(defaultLoadingSymbol));
        try {
          const locationsContextToken = getLocationsContextTokenFromURL();
          await axios.post(`return/${returnID}/email/confirmation`, {
            email,
            ...(locationsContextToken ? { locationsContextToken } : {}),
          });
          onSuccess(email);
        } catch (err) {
          setError(t("emailModalError"));
        } finally {
          dispatch(clearLoadingIndicator());
        }
      }}
      primaryMessage={t("emailModalHeader")}
      subMessage={t("emailModalSubheader")}
      successMessage={t("emailModalSuccess")}
    />
  );
};

const isButtonFocused = ({ returnShoppingItems, isDraftOrderPaid }: ReturnStatusComposite): boolean => {
  if (returnShoppingItems) {
    return !!isDraftOrderPaid;
  }
  return true;
};

const renderDesktopReturnStatus = (returnStatusComposite: ReturnStatusComposite) => {
  const { app } = useSelector(store => store);
  const colors = app?.colors

  return (
    <$NextStepsCard isButtonFocused={isButtonFocused(returnStatusComposite)} primaryColor={colors?.primaryColor}>
      <$PrimaryDetails>
        <$PrimaryDetailsLeftColumn>
          <Header {...returnStatusComposite} />
          <Body {...returnStatusComposite} />
          <NearestLocationsPanel {...returnStatusComposite} />
          <Button {...returnStatusComposite} />
          <Disclaimer {...returnStatusComposite} />
        </$PrimaryDetailsLeftColumn>
        <QRCard {...returnStatusComposite} />
      </$PrimaryDetails>
      <ConfirmationDetails {...returnStatusComposite} />
    </$NextStepsCard>
  );
};

const renderMobileNextStepsCard = (returnStatusComposite: ReturnStatusComposite) => {
  const { app } = useSelector(store => store);
  const colors = app?.colors

  return (
    <$NextStepsCard isButtonFocused={isButtonFocused(returnStatusComposite)} primaryColor={colors?.primaryColor}>
      <$PrimaryDetails>
        <$PrimaryDetailsSingleColumn>
          <Header {...returnStatusComposite} />
          <Body {...returnStatusComposite} />
          <QRCard {...returnStatusComposite} />
          <NearestLocationsPanel {...returnStatusComposite} />
          <Button {...returnStatusComposite} />
          <Disclaimer {...returnStatusComposite} />
        </$PrimaryDetailsSingleColumn>
      </$PrimaryDetails>
      <ConfirmationDetails {...returnStatusComposite} />
    </$NextStepsCard>
  );
};

// TODO CF-994 QR card (EU QR code), confirmation details link functionality
export const NextStepsCard = (returnStatusComposite: ReturnStatusComposite) => {
  const { width } = useViewport();
  if (width < MOBILE_BREAKPOINT) {
    return renderMobileNextStepsCard(returnStatusComposite);
  }
  return renderDesktopReturnStatus(returnStatusComposite);
};
